import { useEffect, useState } from "react";

/*
  BarLoader – flache, lange Ladeanimation ohne Text.
  Ein kleines Haus hüpft hin und her. Am Rand landet es, dreht sich um
  und hüpft zurück. Passt sich der verfügbaren Breite an und animiert
  nur transform und opacity.

  Die Strecke wird über Container-Einheiten (cqw) berechnet. Dadurch
  ragt nichts über den Container hinaus und seine Breite bleibt stabil.

  Props:
    delay     – ms, bevor der Loader erscheint (verhindert Aufblitzen)
    maxWidth  – maximale Breite (Zahl in px oder CSS-Wert), Standard 480
    houseSize – Breite des Hauses in px, Standard 22
*/

const CHIMNEY = "M16 8 V2 H20 V11.3 Z";
const BODY =
  "M0 30 V14 L12 3 L24 14 V30 Z" +
  " M10 30 V22 H14 V30 Z" +
  " M3.5 17 h5 v4 h-5 Z M15.5 17 h5 v4 h-5 Z";

const CYCLE = 3.2; // Sekunden für eine Richtung (hin und zurück = 2 × CYCLE)
const HOPS = 8;    // Sprünge pro Richtung (CYCLE / HOPS = Dauer eines Sprungs)

const CSS = `
.bar-root{
  --bar-color:#2E4A42;
  position:relative; width:100%; margin:0 auto;
  container-type:inline-size;
  height:calc(var(--bar-house) * 1.25 + 14px);
}
@media (prefers-color-scheme: dark){ .bar-root{--bar-color:#CFE0D8} }

.bar-mover{
  position:absolute; left:0; bottom:3px;
  width:var(--bar-house);
  animation:bar-move ${CYCLE * 2}s linear infinite;
}
.bar-face{
  transform-origin:50% 50%;
  animation:bar-face ${CYCLE * 2}s step-end infinite;
}
.bar-hop{
  transform-origin:50% 100%;
  animation:bar-hop ${CYCLE / HOPS}s infinite;
}
.bar-hop svg{display:block; width:100%; height:auto; fill:var(--bar-color); fill-rule:evenodd}
.bar-shadow{
  position:absolute; left:0; bottom:-3px; width:100%; height:3px;
  border-radius:50%; background:var(--bar-color); opacity:.18;
  animation:bar-shadow ${CYCLE / HOPS}s infinite;
}

/* Hin bis 50 %, zurück bis 100 %. Die Wende fällt genau auf eine Landung. */
@keyframes bar-move{
  0%,100%{transform:translateX(0)}
  50%{transform:translateX(calc(100cqw - var(--bar-house)))}
}
/* Blickrichtung: auf dem Rückweg gespiegelt, damit es nach vorn kippt */
@keyframes bar-face{ 0%{transform:scaleX(1)} 50%{transform:scaleX(-1)} }

@keyframes bar-hop{
  0%   {transform:translateY(0) rotate(0) scale(1.08,.9); animation-timing-function:cubic-bezier(.2,.7,.4,1)}
  45%  {transform:translateY(-10px) rotate(8deg) scale(.97,1.04); animation-timing-function:cubic-bezier(.6,0,.8,.4)}
  90%  {transform:translateY(0) rotate(0) scale(1,1)}
  100% {transform:translateY(0) rotate(0) scale(1.08,.9)}
}
@keyframes bar-shadow{
  0%,100% {transform:scaleX(1); opacity:.2}
  45%     {transform:scaleX(.5); opacity:.06}
}

@media (prefers-reduced-motion: reduce){
  .bar-root *{animation:none !important}
  .bar-mover{transform:translateX(calc(50cqw - var(--bar-house) / 2))}
}`;

export default function LoadingAnimation({ delay = 250, maxWidth = 480, houseSize = 22 }) {
  const [visible, setVisible] = useState(delay === 0);

  useEffect(() => {
    if (!delay) return;
    const t = setTimeout(() => setVisible(true), delay);
    return () => clearTimeout(t);
  }, [delay]);

  if (!visible) return null;

  return (
    <div
      className="bar-root"
      role="status"
      aria-busy="true"
      aria-label="Wird geladen"
      style={{ maxWidth, "--bar-house": `${houseSize}px` }}
    >
      <style>{CSS}</style>
      <div className="bar-mover" aria-hidden="true">
        <div className="bar-shadow" />
        <div className="bar-face">
          <div className="bar-hop">
            <svg viewBox="0 0 24 30">
              <path d={CHIMNEY} />
              <path d={BODY} />
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
}